
import { useState, useEffect } from 'react'
import { MessageCircle, Bot, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/components/ui/use-toast"
import { MessageList } from './chat/MessageList'
import { ChatInput } from './chat/ChatInput'
import { useChatState } from '@/hooks/useChatState'

type ChatOptionProps = {
  standalone?: boolean;
}

export default function ChatOption({ standalone = false }: ChatOptionProps) {
  const { toast } = useToast()
  const { messages, setMessages, sessionId } = useChatState()
  const [newMessage, setNewMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim()) return

    const userMessage = {
      id: Date.now().toString(),
      text: newMessage,
      sender: 'user' as const
    }

    setMessages(prev => [...prev, userMessage])
    setNewMessage("")
    setIsLoading(true)

    try {
      // Use the enhanced-chat function instead of chat
      const { data, error } = await supabase.functions.invoke('enhanced-chat', {
        body: { message: newMessage },
      })

      if (error) throw error

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        text: data.response,
        sender: 'assistant' as const
      }

      setMessages(prev => [...prev, assistantMessage])
    } catch (error) {
      console.error('Error:', error)
      toast({
        title: "Error",
        description: "Failed to get response. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const clearChatHistory = async () => {
    try {
      setMessages([])
      localStorage.removeItem('chatHistory')
      toast({
        title: "Success",
        description: "Chat history cleared successfully.",
      })
    } catch (error) {
      console.error('Error clearing chat history:', error)
      toast({
        title: "Error",
        description: "Failed to clear chat history.",
        variant: "destructive",
      })
    }
  }

  if (!isInitialized) {
    return null
  }

  // In standalone mode, we render just the chat content directly
  if (standalone) {
    return (
      <div className="flex flex-col h-full">
        <SheetHeader className="px-6 py-4 border-b sticky top-0 bg-background z-10">
  <div className="flex justify-between items-center">
    <SheetTitle>Metrade Assistant</SheetTitle>
    <div className="flex items-center gap-2">
      {messages.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearChatHistory}
          className="text-sm"
        >
          Clear History
        </Button>
      )}
      <SheetClose asChild>
        <Button variant="ghost" size="icon" className="text-gray-500 hover:text-red-500">
          <X className="h-4 w-4" />
        </Button>
      </SheetClose>
    </div>
  </div>
</SheetHeader>
        
        {/* Use grid for better layout control on mobile */}
        <div className="grid grid-rows-[1fr_auto] h-[calc(100%-76px)]">
          {/* Message list with proper scrolling */}
          <div className="overflow-hidden">
            <MessageList messages={messages} isLoading={isLoading} />
          </div>
          
          {/* Input area fixed at the bottom */}
          <div className="sticky bottom-0 bg-background border-t">
            <ChatInput
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              handleSendMessage={handleSendMessage}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    )
  }

  // Default mode with Sheet wrapper for desktop
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <div className="fixed bottom-4 right-4">
          <div className="absolute -top-10 right-0 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm animate-bounce">
            Ask Me!
          </div>
          <Button
            className="h-12 w-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
            size="icon"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
        </div>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-[500px] p-0">
        <SheetHeader className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <SheetTitle>Metrade Assistant</SheetTitle>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearChatHistory}
                className="text-sm"
              >
                Clear History
              </Button>
            )}
          </div>
        </SheetHeader>
        <div className="flex flex-col h-[calc(100vh-8rem)]">
          <MessageList messages={messages} isLoading={isLoading} />
          <ChatInput
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            handleSendMessage={handleSendMessage}
            isLoading={isLoading}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
